import { ApolloClient } from '@apollo/client';

import { gengql } from '@/__generated__';

const FETCH_PERMISSIONS = gengql(/* GraphQL */ `
  query fetchPermissions($userId: String, $requests: [PermissionRequestInput!]!) {
    permissions(user_id: $userId, requests: $requests) {
      service
      action
      resource
      allowed
    }
  }
`);

export interface PermissionRequest {
  service: string;
  action: string;
  resource: string;
}

export interface Permission extends PermissionRequest {
  allowed: boolean;
}

export class PermissionService {
  private graphql: ApolloClient<any>;
  private userId?: string;

  constructor(graphql: ApolloClient<any>, userId?: string) {
    this.graphql = graphql;
    this.userId = userId;
  }

  async allowed(service: string, action: string, resource: string): Promise<boolean> {
    const req = { service, action, resource };
    const perms = await this.allowedBatch([req]);
    return perms[0].allowed;
  }

  async allowedBatch(requests: PermissionRequest[]): Promise<Permission[]> {
    const filteredRequests = requests.filter(
      (req) => req.resource !== undefined && req.resource !== null
    );
    if (filteredRequests.length === 0) return [];

    // Snapshot the userId at call time so a mid-flight rebindUserId() (see
    // the doc on rebindUserId below) can't silently corrupt the caller's
    // result. The HTTP request below carries `requestedUserId` into the
    // GraphQL variables; on resolve we check it against `this.userId` and
    // throw if a rebind landed during the await. Callers (route loaders,
    // permission hooks) are expected to either retry or treat the rejection
    // as a transient auth event — which matches the real semantics of
    // "permissions for who?" changing under them.
    const requestedUserId = this.userId;

    const { data } = await this.graphql.query({
      query: FETCH_PERMISSIONS,
      variables: {
        userId: requestedUserId,
        requests: filteredRequests,
      },
    });

    if (this.userId !== requestedUserId) {
      throw new Error(
        'PermissionService.allowedBatch: userId was rebound mid-flight; ' +
          'discarding stale permission results. Retry from the new identity.'
      );
    }

    return (data.permissions || []).map((p) => p!);
  }

  forUser(userId: string): PermissionService {
    return new PermissionService(this.graphql, userId);
  }

  // rebindUserId mutates the userId on this instance in place.
  //
  // CAUTION: shared mutable state. The app boots its PermissionService
  // with viewer.id (which is "" for a logged-out self-service signup) and
  // captures the reference in the router middleware context — there's no
  // path for routes to receive a freshly-constructed service
  // post-authentication. After authSession.authenticate establishes a
  // real session, rebinding the existing instance is the minimal surgery:
  // any captured reference (router context, hooks, redux thunks) sees the
  // updated userId on the next call without having to re-thread a new
  // instance through every call site.
  //
  // Concurrency semantics (corrected from a previous version of this
  // comment, which incorrectly claimed JS single-threading made the rebind
  // safe). allowedBatch() captures `this.userId` synchronously into the
  // GraphQL variables, then awaits the HTTP response. If rebindUserId()
  // fires while that promise is in flight, the response resolves with
  // results for the PRE-rebind identity — caller would otherwise treat
  // pre-rebind permissions as authoritative for the new identity. The
  // guard inside allowedBatch() detects this case (this.userId !==
  // requestedUserId on resolve) and throws, forcing the caller to retry
  // from the new identity rather than silently consume stale data.
  //
  // rebindUserId is intended to be called only from
  // authSession.authenticate at well-defined mid-session boundaries; do
  // not invoke from concurrent code paths.
  rebindUserId(userId: string): void {
    this.userId = userId;
  }
}

// Module-level reference to the boot-time PermissionService instance so
// authSession.authenticate can call rebindUserId after a mid-session
// auth event (sign-up completion, team-join auto-approve, etc.).
// Set once during app boot in src/index.tsx after instantiation; read
// from auth/utils/authSession.ts. Don't read this from React components
// or thunks — they should accept PermissionService as an argument
// (as they do today via router middleware context).
let bootInstance: PermissionService | null = null;

export function registerPermissionService(svc: PermissionService): void {
  bootInstance = svc;
}

export function getPermissionService(): PermissionService | null {
  return bootInstance;
}
